import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import styles from "./EngineerForm.module.css";

function EngineerForm() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [visitType, setVisitType] = useState("primary");

  const [primaryPrograms, setPrimaryPrograms] = useState([]);
  const [repeatPrograms, setRepeatPrograms] = useState([]);
  const [program, setProgram] = useState("");
  const [programId, setProgramId] = useState("");

  const [location, setLocation] = useState([]);
  const [locationCustom, setLocationCustom] = useState("");

  const [place, setPlace] = useState([]);
  const [placeCustom, setPlaceCustom] = useState("");

  const [purpose, setPurpose] = useState("");
  const [decoration, setDecoration] = useState("");

  const [step, setStep] = useState([]);
  const [stepCustom, setStepCustom] = useState("");

  const [floor, setFloor] = useState([]);
  const [floorCustom, setFloorCustom] = useState("");

  const [ballast, setBallast] = useState([]);

  const [ferma, setFerma] = useState([]);
  const [fermaColor, setFermaColor] = useState([]);
  const [fermaNotes, setFermaNotes] = useState("");

  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("Заявка отправлена");

  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const clearError = () => {
    setErrorTitle("");
    setErrorMessage("");
  };

  useEffect(() => {
    if (!currentUser) navigate("/");
  }, [currentUser, navigate]);

  const programs = visitType === "primary" ? primaryPrograms : repeatPrograms;

  useEffect(() => {
    setProgram("");
    setProgramId("");
  }, [visitType]);

  const toggleInArray = (arr, value) => {
    if (arr.includes(value)) return arr.filter((x) => x !== value);
    return [...arr, value];
  };

  const showColors =
    ferma.includes("H-30") || ferma.includes("H-40") || ferma.includes("S-52");

  const isPrimary = visitType === "primary";
  const shouldValidatePlace = !location.includes("room");

  const groupHasChecked = (arr) => arr.length > 0;

  const customCheckedNeedsText = (arr, customValue, customText) => {
    if (arr.includes(customValue)) return customText.trim().length > 0;
    return true;
  };

  const colorsValid = () => {
    if (!showColors) return true;
    return fermaColor.includes("silver") || fermaColor.includes("black");
  };

  const programSelected = program !== "";

  const validGroupsPrimary =
    groupHasChecked(location) &&
    (!shouldValidatePlace || groupHasChecked(place)) &&
    groupHasChecked(step) &&
    groupHasChecked(floor) &&
    groupHasChecked(ballast) &&
    groupHasChecked(ferma);

  const customInputsValidPrimary =
    customCheckedNeedsText(location, "custom", locationCustom) &&
    (!shouldValidatePlace ||
      customCheckedNeedsText(place, "custom", placeCustom)) &&
    customCheckedNeedsText(step, "custom", stepCustom) &&
    customCheckedNeedsText(floor, "custom", floorCustom);

  const isFormValid = useMemo(() => {
    if (!programSelected) return false;
    if (!isPrimary) return true;
    return validGroupsPrimary && customInputsValidPrimary && colorsValid();
  }, [
    programSelected,
    isPrimary,
    validGroupsPrimary,
    customInputsValidPrimary,
    fermaColor,
    showColors,
  ]);

  useEffect(() => {
    if (!showColors) setFermaColor([]);
  }, [showColors]);

  const handleBallastChange = (value) => {
    setBallast((prev) => {
      if (value === "bez-ballast") {
        return prev.includes("bez-ballast") ? [] : ["bez-ballast"];
      }

      return toggleInArray(
        prev.filter((item) => item !== "bez-ballast"),
        value
      );
    });
  };

  const handleFermaChange = (value) => {
    setFerma((prev) => {
      if (value === "bez-ferm") {
        return prev.includes("bez-ferm") ? [] : ["bez-ferm"];
      }

      return toggleInArray(
        prev.filter((item) => item !== "bez-ferm"),
        value
      );
    });
  };

  useEffect(() => {
    let cancelled = false;
    setLoadingPrograms(true);

    (async () => {
      try {
        const resp = await fetch("/api/events/active-list", {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        const data = await safeReadResponse(resp);

        if (!resp.ok) {
          if (!cancelled) {
            setPrimaryPrograms([]);
            setRepeatPrograms([]);
            setErrorTitle("Не удалось загрузить программы");
            setErrorMessage(normalizeErrorMessage(resp.status, data));
          }
          return;
        }

        const grouped = data?.grouped || {
          current:
            data?.current ||
            data?.events?.filter((e) => e.relevance === "current") ||
            [],
          future:
            data?.future ||
            data?.events?.filter((e) => e.relevance === "future") ||
            [],
        };

        const activeEventsRaw = [
          ...(Array.isArray(grouped.current) ? grouped.current : []),
          ...(Array.isArray(grouped.future) ? grouped.future : []),
        ];

        // дедупликация по id
        const uniqueMap = new Map();

        activeEventsRaw.forEach((item) => {
          if (!item || !item.id || !item.name || !String(item.name).trim())
            return;
          if (!uniqueMap.has(String(item.id))) {
            uniqueMap.set(String(item.id), item);
          }
        });

        const activeEvents = Array.from(uniqueMap.values());

        const primary = activeEvents.filter(
          (item) => item?.secondRecord === false
        );
        const repeat = activeEvents.filter(
          (item) => item?.secondRecord === true
        );

        if (!cancelled) {
          setPrimaryPrograms(primary);
          setRepeatPrograms(repeat);
        }
      } catch (err) {
        if (!cancelled) {
          setPrimaryPrograms([]);
          setRepeatPrograms([]);
          setErrorTitle("Ошибка сети");
          setErrorMessage(
            "Не удалось загрузить программы. " + (err?.message || "")
          );
        }
      } finally {
        if (!cancelled) setLoadingPrograms(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!successOpen) return;
    const t = setTimeout(() => navigate("/dashboard"), 3000);
    return () => clearTimeout(t);
  }, [successOpen, navigate]);

  async function safeReadResponse(response) {
    const ct = response.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      try {
        return await response.json();
      } catch {
        return {};
      }
    }
    try {
      const text = await response.text();
      return { details: text };
    } catch {
      return {};
    }
  }

  function normalizeErrorMessage(status, data) {
    const raw =
      data?.details ??
      data?.error ??
      data?.message ??
      (typeof data === "string" ? data : "");

    const asText = typeof raw === "string" ? raw : JSON.stringify(raw);

    if (status === 403) return asText || "Недостаточно прав";
    if (status === 401) return asText || "Требуется авторизация";
    if (status >= 500) return asText || "Ошибка сервера";
    return asText || "Ошибка при отправке";
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    clearError();
    setSuccessOpen(false);

    if (!isFormValid) {
      setErrorTitle("Проверьте поля");
      setErrorMessage("Заполните обязательные поля.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        visitType,
        program,
        programId,
        location,
        locationCustom,
        place,
        placeCustom,
        purpose,
        decoration,
        step,
        stepCustom,
        floor,
        floorCustom,
        ballast,
        ferma,
        fermaColor,
        fermaNotes,
      };

      const resp = await fetch("/api/engineer/record-to-engineer", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await safeReadResponse(resp);

      if (resp.ok) {
        const msg =
          data?.details || data?.message || "Запрос успешно отправлен";
        setSuccessMessage(typeof msg === "string" ? msg : JSON.stringify(msg));
        setSuccessOpen(true);
      } else {
        setErrorTitle("Не удалось отправить");
        setErrorMessage(normalizeErrorMessage(resp.status, data));
      }
    } catch (err) {
      setErrorTitle("Ошибка сети");
      setErrorMessage(
        "Не удалось связаться с сервером. " + (err?.message || "")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentUser) return <div>Загрузка...</div>;

  return (
    <div className={styles.container}>
      {successOpen && (
        <div className={styles.successOverlay} role="dialog" aria-modal="true">
          <div className={styles.successModal}>
            <div className={styles.successIcon} aria-hidden="true" />
            <h3>Успешно!</h3>
            <p>{successMessage}</p>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} />
            </div>
            <p className={styles.redirectText}>
              Переход в кабинет через 3 секунды...
            </p>
          </div>
        </div>
      )}

      <div className={styles.formContainer}>
        <form className={styles.form} id="engineerForm" onSubmit={handleSubmit}>
          <div className={styles.topControls}>
            <div className={styles.toggle}>
              <input
                type="radio"
                id="primary"
                name="visit_type"
                value="primary"
                checked={visitType === "primary"}
                onChange={() => setVisitType("primary")}
                className={styles.toggleInput}
              />
              <label
                htmlFor="primary"
                className={`${styles.toggleLabel} ${
                  visitType === "primary" ? styles.active : ""
                }`}
              >
                Первичное
              </label>

              <input
                type="radio"
                id="repeat"
                name="visit_type"
                value="repeat"
                checked={visitType === "repeat"}
                onChange={() => setVisitType("repeat")}
                className={styles.toggleInput}
              />
              <label
                htmlFor="repeat"
                className={`${styles.toggleLabel} ${
                  visitType === "repeat" ? styles.active : ""
                }`}
              >
                Повторное
              </label>
            </div>

            <button
              className={styles.backButton}
              onClick={() => navigate("/dashboard")}
              type="button"
            >
              Назад
            </button>
          </div>

          <div className={styles.inputField}>
            <label className={styles.label} htmlFor="program">
              Программа: *
            </label>
            <select
              className={styles.select}
              name="program"
              id="program"
              value={program}
              onChange={(e) => {
                const selectedIndex = e.target.selectedIndex;
                const selectedOption = e.target.options[selectedIndex];
                setProgram(e.target.value);
                setProgramId(selectedOption.dataset.id || "");
              }}
              required
              disabled={loadingPrograms || submitting}
            >
              <option value="">
                {loadingPrograms ? "Загрузка..." : "Выберите программу"}
              </option>

              {programs.map((item, idx) => {
                const value =
                  typeof item === "string" ? item : item?.name || "";
                const id = typeof item === "string" ? "" : item?.id || "";
                if (!value) return null;

                return (
                  <option key={`${value}-${idx}`} value={value} data-id={id}>
                    {value}
                  </option>
                );
              })}
            </select>
            <input type="hidden" name="programId" value={programId} />
          </div>

          {isPrimary && (
            <div className={styles.section}>
              <label className={styles.labelMain}>Улица или помещение:</label>
              <div className={styles.checkboxGroup}>
                <div className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    id="street"
                    value="street"
                    checked={location.includes("street")}
                    onChange={() =>
                      setLocation((p) => toggleInArray(p, "street"))
                    }
                  />
                  <label htmlFor="street" className={styles.checkboxLabel}>
                    Улица
                  </label>
                </div>

                <div className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    id="room"
                    value="room"
                    checked={location.includes("room")}
                    onChange={() =>
                      setLocation((p) => toggleInArray(p, "room"))
                    }
                  />
                  <label htmlFor="room" className={styles.checkboxLabel}>
                    Помещение
                  </label>
                </div>

                <div className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    id="custom_loc"
                    value="custom"
                    checked={location.includes("custom")}
                    onChange={() =>
                      setLocation((p) => toggleInArray(p, "custom"))
                    }
                  />
                  <label
                    htmlFor="custom_loc"
                    className={`${styles.checkboxLabel} ${styles.green}`}
                  >
                    Свой вариант
                  </label>
                </div>
              </div>

              <input
                type="text"
                className={`${styles.input} ${styles.customInput} ${
                  location.includes("custom") ? styles.show : ""
                }`}
                placeholder="Помещение"
                value={locationCustom}
                onChange={(e) => setLocationCustom(e.target.value)}
                disabled={submitting}
              />

              <label className={styles.labelMain}>Место в городе:</label>
              <div className={styles.checkboxGroup}>
                {[
                  { id: "water", value: "water", text: "У воды" },
                  { id: "field", value: "field", text: "В поле" },
                  { id: "square", value: "square", text: "На площади" },
                  { id: "city", value: "city", text: "Городская застройка" },
                  { id: "building", value: "building", text: "Вокруг здания" },
                ].map((x) => (
                  <div className={styles.checkboxItem} key={x.id}>
                    <input
  type="checkbox"
  id={x.id}
  value={x.value}
  checked={place.includes(x.value)}
  disabled={!shouldValidatePlace || submitting}
  onChange={() => setPlace((p) => toggleInArray(p, x.value))}
/>
                    <label htmlFor={x.id} className={styles.checkboxLabel}>
                      {x.text}
                    </label>
                  </div>
                ))}

                <div className={styles.checkboxItem}>
                  <input
  type="checkbox"
  id="custom_place_check"
  value="custom"
  checked={place.includes("custom")}
  disabled={!shouldValidatePlace || submitting}
  onChange={() => setPlace((p) => toggleInArray(p, "custom"))}
/>
                  <label
                    htmlFor="custom_place_check"
                    className={`${styles.checkboxLabel} ${styles.green}`}
                  >
                    Свой вариант
                  </label>
                </div>
              </div>

              <input
  type="text"
  className={`${styles.input} ${styles.customInput} ${
    place.includes("custom") ? styles.show : ""
  }`}
  placeholder="Место в городе"
  value={placeCustom}
  onChange={(e) => setPlaceCustom(e.target.value)}
  disabled={!shouldValidatePlace || submitting}
/>
            </div>
          )}

          {isPrimary && (
            <div className={styles.section}>
              <label className={styles.labelSecondary}>
                Предназначение конструкции:
              </label>
              <textarea
                className={`${styles.input} ${styles.textarea}`}
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                disabled={submitting}
              />

              <label className={styles.labelSecondary}>
                Будут ли баннеры или декорации? Где и какие?
              </label>
              <textarea
                className={`${styles.input} ${styles.textarea}`}
                value={decoration}
                onChange={(e) => setDecoration(e.target.value)}
                disabled={submitting}
              />
            </div>
          )}

          {isPrimary && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                Какое оборудование использовать при проектировании?
              </h3>

              <label className={styles.labelSecondary}>Шаг металла:</label>
              <div className={styles.checkboxGroup}>
                {[
                  { id: "step_any", value: "any", text: "Без разницы" },
                  { id: "step_2", value: "2", text: "2" },
                  { id: "step_207", value: "2.07", text: "2.07" },
                  { id: "step_25", value: "2.5", text: "2.5" },
                  { id: "step_257", value: "2.57", text: "2.57" },
                ].map((x) => (
                  <div className={styles.checkboxItem} key={x.id}>
                    <input
                      type="checkbox"
                      id={x.id}
                      value={x.value}
                      checked={step.includes(x.value)}
                      onChange={() => setStep((p) => toggleInArray(p, x.value))}
                    />
                    <label htmlFor={x.id} className={styles.checkboxLabel}>
                      {x.text}
                    </label>
                  </div>
                ))}

                <div className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    id="step_custom_check"
                    value="custom"
                    checked={step.includes("custom")}
                    onChange={() => setStep((p) => toggleInArray(p, "custom"))}
                  />
                  <label
                    htmlFor="step_custom_check"
                    className={`${styles.checkboxLabel} ${styles.green}`}
                  >
                    Свой вариант
                  </label>
                </div>
              </div>

              <input
                type="text"
                className={`${styles.input} ${styles.customInput} ${
                  step.includes("custom") ? styles.show : ""
                }`}
                placeholder="Шаг металла"
                value={stepCustom}
                onChange={(e) => setStepCustom(e.target.value)}
                disabled={submitting}
              />

              <label className={styles.labelSecondary}>Покрытие пола:</label>
              <div className={styles.checkboxGroup}>
                {[
                  { id: "floor_machines", value: "machines", text: "Станки" },
                  { id: "floor_plywood", value: "plywood", text: "Фанера" },
                ].map((x) => (
                  <div className={styles.checkboxItem} key={x.id}>
                    <input
                      type="checkbox"
                      id={x.id}
                      value={x.value}
                      checked={floor.includes(x.value)}
                      onChange={() =>
                        setFloor((p) => toggleInArray(p, x.value))
                      }
                    />
                    <label htmlFor={x.id} className={styles.checkboxLabel}>
                      {x.text}
                    </label>
                  </div>
                ))}

                <div className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    id="floor_custom_check"
                    value="custom"
                    checked={floor.includes("custom")}
                    onChange={() => setFloor((p) => toggleInArray(p, "custom"))}
                  />
                  <label
                    htmlFor="floor_custom_check"
                    className={`${styles.checkboxLabel} ${styles.green}`}
                  >
                    Свой вариант
                  </label>
                </div>
              </div>

              <input
                type="text"
                className={`${styles.input} ${styles.customInput} ${
                  floor.includes("custom") ? styles.show : ""
                }`}
                placeholder="Покрытие пола"
                value={floorCustom}
                onChange={(e) => setFloorCustom(e.target.value)}
                disabled={submitting}
              />

              <label className={styles.labelSecondary}>Балласт:</label>
              <div className={styles.checkboxGroup}>
                {[
                  {
                    id: "ballast_none",
                    value: "bez-ballast",
                    text: "Без балласта",
                  },
                  { id: "ballast_concrete", value: "concrete", text: "Бетон" },
                  { id: "ballast_steel", value: "steel", text: "Сталь" },
                  { id: "ballast_water", value: "water", text: "Водоналивной" },
                  { id: "ballast_manual", value: "manual", text: "Ручной" },
                ].map((x) => {
                  return (
                    <div className={styles.checkboxItem} key={x.id}>
                      <input
                        type="checkbox"
                        id={x.id}
                        value={x.value}
                        checked={ballast.includes(x.value)}
                        onChange={() => handleBallastChange(x.value)}
                      />
                      <label htmlFor={x.id} className={styles.checkboxLabel}>
                        {x.text}
                      </label>
                    </div>
                  );
                })}
              </div>

              <label className={styles.labelSecondary}>Фермы:</label>
              <div className={styles.checkboxGroup}>
                {[
                  { id: "bez_ferm", value: "bez-ferm", text: "Без ферм" },
                  { id: "ferma_H30", value: "H-30", text: "H-30" },
                  { id: "ferma_S36", value: "S-36", text: "S-36" },
                  { id: "ferma_H40", value: "H-40", text: "H-40" },
                  { id: "ferma_S52", value: "S-52", text: "S-52" },
                  { id: "ferma_B100", value: "B-100", text: "B-100" },
                  { id: "ferma_MF75", value: "MF-75", text: "МФ-75" },
                  { id: "ferma_MF160", value: "MF-160", text: "МФ-160" },
                ].map((x) => {
                  return (
                    <div className={styles.checkboxItem} key={x.id}>
                      <input
                        type="checkbox"
                        id={x.id}
                        value={x.value}
                        checked={ferma.includes(x.value)}
                        onChange={() => handleFermaChange(x.value)}
                      />
                      <label htmlFor={x.id} className={styles.checkboxLabel}>
                        {x.text}
                      </label>
                    </div>
                  );
                })}
              </div>

              {showColors && (
                <div className={styles.colorSelector}>
                  <div className={styles.colorButtons}>
                    <div className={styles.checkboxItem}>
                      <input
                        type="checkbox"
                        id="color_silver"
                        value="silver"
                        checked={fermaColor.includes("silver")}
                        onChange={() =>
                          setFermaColor((p) => toggleInArray(p, "silver"))
                        }
                      />
                      <label
                        htmlFor="color_silver"
                        className={`${styles.checkboxLabel} ${styles.silver}`}
                      >
                        Серебряный
                      </label>
                    </div>

                    <div className={styles.checkboxItem}>
                      <input
                        type="checkbox"
                        id="color_black"
                        value="black"
                        checked={fermaColor.includes("black")}
                        onChange={() =>
                          setFermaColor((p) => toggleInArray(p, "black"))
                        }
                      />
                      <label
                        htmlFor="color_black"
                        className={`${styles.checkboxLabel} ${styles.black}`}
                      >
                        Черный
                      </label>
                    </div>
                  </div>

                  {!colorsValid() && (
                    <div className={styles.hintError}>Выберите цвет ферм</div>
                  )}
                </div>
              )}

              <label className={styles.labelSecondary}>
                Пожелания по фермовым элементам:
              </label>
              <textarea
                className={`${styles.input} ${styles.textarea}`}
                value={fermaNotes}
                onChange={(e) => setFermaNotes(e.target.value)}
                disabled={submitting}
              />
            </div>
          )}

          {(errorTitle || errorMessage) && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              <div>
                <p className={styles.alertText}>{errorTitle || "Ошибка"}</p>
                {errorMessage && (
                  <p className={styles.alertSubText}>{errorMessage}</p>
                )}
              </div>
              <button
                type="button"
                className={styles.alertButton}
                onClick={clearError}
              >
                Понятно
              </button>
            </div>
          )}

          <button
            type="submit"
            className={styles.sendButton}
            disabled={!isFormValid || submitting}
          >
            {submitting ? "ОТПРАВКА..." : "ЗАПИСАТЬСЯ"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default EngineerForm;
